import React from 'react';

/**
 * lazyWithRetry — a drop-in `React.lazy` replacement that survives a post-deploy
 * hashed-chunk rotation instead of white-screening a long-lived tab.
 *
 * THE BUG THIS FIXES (grand-review P1-14b): a failed dynamic `import()` is FATAL
 * under Suspense — it throws to the nearest ErrorBoundary, so the user sees
 * "Something went wrong" instead of the page. This happens routinely when a new
 * build ships while a tab is open: the old `index-<hash>.js` chunks are gone from
 * the CDN, so navigating to a not-yet-loaded route 404s the chunk. It's worst on
 * the parent revenue path (dashboard / billing / checkout).
 *
 * Recovery ladder, mirroring what a human would do:
 *   1. Retry the import ONCE after a short delay — a transient blip self-heals.
 *   2. If it still fails, do a ONE-TIME full-page reload so the browser fetches
 *      the current index.html + the current chunk hashes. Guarded by a
 *      sessionStorage flag keyed to this session so a genuinely-broken build (or
 *      an offline device) can't spin in a reload loop — after one reload we give
 *      up and re-throw so the ErrorBoundary shows its message.
 *
 * The public API is identical to `React.lazy(importFn)`.
 */

const RELOAD_FLAG_PREFIX = 'lwa_chunk_reload_v1';

/** A dynamic-import failure caused by a missing/stale chunk (vs. a real code error). */
function isChunkLoadError(err: unknown): boolean {
  const e = err as { name?: string; message?: string; code?: string } | null;
  const name = e?.name ?? '';
  const message = e?.message ?? '';
  if (name === 'ChunkLoadError') return true;
  // Vite/webpack/browsers all phrase the missing-module failure differently.
  return (
    /ChunkLoadError/i.test(message) ||
    /Loading chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

function safeSession(): Storage | null {
  try {
    return (globalThis as unknown as { sessionStorage?: Storage }).sessionStorage ?? null;
  } catch {
    return null;
  }
}

function reloadOnce(key: string): boolean {
  const store = safeSession();
  const loc = (globalThis as unknown as { location?: { reload?: () => void } }).location;
  if (!store || typeof loc?.reload !== 'function') return false;
  const flag = `${RELOAD_FLAG_PREFIX}:${key}`;
  try {
    if (store.getItem(flag) === '1') return false; // already reloaded once this session — don't loop
    store.setItem(flag, '1');
  } catch {
    return false; // storage unavailable → don't risk a reload we can't guard
  }
  loc.reload();
  return true;
}

/** Clear the reload guard for a chunk after a SUCCESSFUL load (so a later, real
 *  stale-chunk event in the same session can still recover once). */
function clearReloadFlag(key: string): void {
  const store = safeSession();
  if (!store) return;
  try {
    store.removeItem(`${RELOAD_FLAG_PREFIX}:${key}`);
  } catch {
    /* ignore */
  }
}

export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  /** Optional stable key so each route's reload guard is independent. Defaults to
   *  the factory's stringified source, which is stable per call-site. */
  key?: string,
): React.LazyExoticComponent<T> {
  const guardKey = key ?? factory.toString().slice(0, 120);
  return React.lazy(() =>
    factory()
      .then((mod) => {
        clearReloadFlag(guardKey);
        return mod;
      })
      .catch((err) => {
        if (!isChunkLoadError(err)) throw err; // a real code error — surface it
        // 1) Retry once after a brief delay.
        return new Promise<{ default: T }>((resolve, reject) => {
          setTimeout(() => {
            factory()
              .then((mod) => {
                clearReloadFlag(guardKey);
                resolve(mod);
              })
              .catch((err2) => {
                // 2) Still failing → one-time full reload to pull the fresh build.
                if (isChunkLoadError(err2) && reloadOnce(guardKey)) {
                  // A reload is underway; keep the promise pending so Suspense
                  // shows the fallback rather than the crash boundary until the
                  // navigation happens.
                  return;
                }
                reject(err2);
              });
          }, 400);
        });
      }),
  );
}
